<template>
  <div id="app">
    <!-- 根據路由 meta 決定是否顯示通用佈局 -->
    <template v-if="!$route.meta.isBare">
      <!-- Loading 狀態 -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>正在載入...</p>
      </div>
      
      <!-- 未安裝狀態 -->
      <div v-else-if="!isInstalled" class="not-installed-container">
        <div class="icon">
          <img src="/logo.png" alt="NewebPay" style="width: 64px; height: 64px;">
        </div>
        <h2>NewebPay 付款整合</h2>
        <p>請先安裝此應用程式以開始使用 NewebPay 付款服務。</p>
        <button @click="installApp" class="install-btn">安裝應用程式</button>
      </div>
      
      <!-- 已安裝狀態 (且非裸露模式) -->
      <div v-else class="installed-container">
        <router-view />
      </div>
    </template>
    
    <!-- 裸露模式下，只渲染 router-view -->
    <router-view v-else />
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      loading: true,
      isInstalled: false,
  userData: null,
  fallbackHandled: false
    }
  },
  async mounted() {
    console.log("App mounted, starting initialization...");
    
    // 使用更直接的方法檢查是否為付款頁面
    const currentPath = window.location.pathname;
  const isPaymentPage = currentPath.includes('/checkout') || 
             currentPath.includes('/newebpay') || 
             currentPath.includes('/payment-return') ||
             currentPath.includes('/payment-method-return') ||
             currentPath.includes('/add-payment-method') ||
             currentPath.includes('/customer-return') ||
             currentPath.includes('/subscription/return') ||
             currentPath.includes('/api/subscription/return');

    console.log("Current path:", currentPath);
    console.log("Is payment page:", isPaymentPage);
    
  // 檢查當前路由是否為 bare 模式（如付款頁面）
  if (this.$route?.meta?.isBare || isPaymentPage) {
      console.log("Bare mode route detected, skipping user data fetch");
      this.loading = false;
      return;
    }
    
    try {
      // 檢查是否在 iframe 環境中
      const isInIframe = window !== window.parent;
      console.log("Is in iframe:", isInIframe);
      
      // 先顯示一些基本內容，避免完全空白
      setTimeout(() => {
        if (this.loading && !this.fallbackHandled) {
          if (isInIframe) {
            // 僅在 iframe 中才導向付款頁，避免正式環境直接造訪時發生導向迴圈
            console.log("Still loading after 3 seconds in iframe, redirecting to /newebpay/checkout");
            this.fallbackHandled = true;
            this.loading = false;
            try { this.$router.push('/newebpay/checkout'); } catch (e) { console.log('route push ignored'); }
          } else {
            console.log("Still loading after 3 seconds outside iframe, show install view instead of redirect");
            this.loading = false;
            this.isInstalled = false; // 顯示安裝界面作為 fallback
          }
        }
      }, 3000);
      
      // 取得使用者資料
      console.log("Attempting to get user data...");
      try {
        const data = await window.ghl.getUserData();
        console.log("user-details", data);
        this.userData = data;
        
        // 檢查是否已安裝
        await this.checkInstallationStatus();
      } catch (userDataError) {
        console.log("getUserData failed:", userDataError.message);
        const msg = (userDataError?.message || '').toLowerCase();
        const isInIframeNow = window !== window.parent;
        // 僅當在 iframe 中且是典型訊息才導向結帳頁
        if (isInIframeNow && (msg.includes('timeout waiting for user data') || msg.includes('not available on payment page'))) {
          this.fallbackHandled = true;
          this.loading = false;
          try { this.$router.push('/newebpay/checkout'); } catch (e) { console.log('route push ignored'); }
          return;
        }
        // 非 iframe 或其他錯誤：停留在安裝/設定入口，不自動導向
        this.loading = false;
        this.isInstalled = false;
        return;
      }
      
    } catch (error) {
      console.error("Failed to get user data:", error);
      // 在預覽模式下顯示示例界面
      this.showPreviewMode();
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async checkInstallationStatus() {
      try {
        if (!this.userData) {
          console.error("No user data available");
          return;
        }
        
        // 檢查並使用 activeLocation 作為 locationId（如果需要）
        let locationId = this.userData.locationId;
        if (!locationId && this.userData.activeLocation) {
          locationId = this.userData.activeLocation;
          console.log("Using activeLocation as locationId:", locationId);
        }
        
        if (!locationId) {
          console.error("No locationId or activeLocation found in user data:", this.userData);
          return;
        }
        
        // 檢查安裝狀態 API
        const response = await fetch(`/api/check-installation?locationId=${locationId}`);
        const result = await response.json();
        
        this.isInstalled = result.isInstalled;
        
        // 如果已安裝，導向設定頁面
        if (this.isInstalled && this.$route.path === '/') {
          console.log("App is installed, redirecting to settings page");
          this.$router.push('/settings');
        } else if (this.isInstalled && this.$route.path === '/custom-provider') {
          console.log("App is installed and on custom-provider path, showing settings");
          // 在 custom-provider 路徑下直接顯示設定
        }
      } catch (error) {
        console.error("Failed to check installation status:", error);
        // 預設為未安裝狀態
        this.isInstalled = false;
      }
    },
    
    showPreviewMode() {
      console.log("Showing preview mode");
      // 在預覽模式下，顯示已安裝狀態並導向設定頁面
      this.isInstalled = true;
      this.userData = {
        locationId: 'preview-location-id',
        companyId: 'preview-company-id',
        userId: 'preview-user-id'
      };
      
      // 導向設定頁面以顯示應用功能
      if (this.$route.path === '/') {
        this.$router.push('/settings').catch(() => {
          console.log("Router navigation handled");
        });
      } else if (this.$route.path === '/custom-provider') {
        console.log("Already on custom-provider path, will show settings content");
        // 在 custom-provider 路徑下不需要重新導向
      }
    },
    
    installApp() {
      // 檢查並使用 activeLocation 作為 locationId（如果需要）
      let locationId = this.userData?.locationId;
      if (!locationId && this.userData?.activeLocation) {
        locationId = this.userData.activeLocation;
      }
      
      if (!locationId) {
        console.error("No location ID available for installation");
        return;
      }
      
      // 開啟安裝流程
      const installUrl = `/ghl/oauth/install?locationId=${locationId}`;
      window.parent.location.href = installUrl;
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.loading-container, .not-installed-container, .installed-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.loading-container {
  text-align: center;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.not-installed-container {
  text-align: center;
  max-width: 400px;
}

.not-installed-container .icon {
  margin-bottom: 1.5rem;
}

.not-installed-container h2 {
  color: #333;
  margin-bottom: 1rem;
}

.not-installed-container p {
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.5;
}

.install-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.install-btn:hover {
  background-color: #0056b3;
}

.installed-container {
  width: 100%;
  height: 100%;
}
</style>
